import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';

export interface MoveAssetsResult {
  movedCount: number;
  assetIds: string[];
  destinationLocationId: string;
  processedAt: string;
}

export interface BarcodeMapping {
  id: string;
  barcode_value: string;
  catalog_id: string;
  location_id: string;
  created_at: string;
  updated_at: string;
}

export interface AdjustAssetTypeResult {
  processedAt: string;
  adjustedQuantity: number;
  targetCatalogId: string;
  destinationLocationId: string;
  sourceCatalogId: string;
  sourceLocationId: string;
  disposedAssetIds: string[];
  createdAssetIds: string[];
  transactionLogId?: string;
  da4949GeneratedDocumentId?: string;
  da4949DocumentEntityId?: string;
  da4949DocumentNumber?: string | null;
}

const readLocationId = (
  source: Record<string, unknown>,
  key: 'primary_location_id' | 'secondary_location_id',
): string | null => {
  const value = source[key];
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

export const AssetService = {
  moveAssets: async (
    assetIds: string[],
    destinationLocationId: string,
  ): Promise<MoveAssetsResult> => {
    return httpClient.post<MoveAssetsResult>('/api/rest/app/v1/assets/move', {
      assetIds,
      destinationLocationId,
    });
  },
  getPreferredLocations: async (catalogId: string): Promise<string[]> => {
    try {
      const [mappings, catalogEntities] = await Promise.all([
        httpClient.get<BarcodeMapping[]>(
          `/api/rest/data/v1/barcode_mappings?catalog_id=eq.${catalogId}`,
        ),
        httpClient.get<{ id: string; entity_data: Record<string, unknown> }[]>(
          `/api/rest/data/v1/entities?id=eq.${catalogId}&select=id,entity_data`,
        ),
      ]);

      const locationIds = new Set(mappings.map((m) => m.location_id));

      // Include catalog default locations (primary/secondary).
      const catalogData = catalogEntities[0]?.entity_data;
      if (catalogData) {
        const primaryLocationId = readLocationId(catalogData, 'primary_location_id');
        const secondaryLocationId = readLocationId(catalogData, 'secondary_location_id');

        if (primaryLocationId) {
          locationIds.add(primaryLocationId);
        }
        if (secondaryLocationId) {
          locationIds.add(secondaryLocationId);
        }
      }

      return [...locationIds];
    } catch (_error) {
      // Return empty array if fetch fails - graceful degradation
      return [];
    }
  },
  getOrCreateBarcodeMapping: async (
    catalogId: string,
    locationId: string,
  ): Promise<BarcodeMapping | null> => {
    const result = await httpClient.post<BarcodeMapping[]>(
      '/api/rest/data/v1/rpc/get_or_create_barcode_mapping',
      {
        p_catalog_id: catalogId,
        p_location_id: locationId,
      },
    );

    return result[0] ?? null;
  },
  adjustAssetType: async (payload: {
    assetIds: string[];
    quantity: number;
    targetCatalogId: string;
    destinationLocationId: string;
    note?: string;
    justification?: string;
  }): Promise<AdjustAssetTypeResult> => {
    return httpClient.post<AdjustAssetTypeResult>(API_CONFIG.ENDPOINTS.ASSETS_ADJUST_TYPE, payload);
  },
};
